<template>
  <div class="matches-page">
    <!-- 顶部广告 -->
    <AdBanner v-if="$config.region !== 'cn'" page="matches" type="banner" />

    <!-- 顶部筛选 tag -->
    <div class="match-filter-bar">
      <div class="match-filter-tags">
        <span
          v-for="tag in computedFilterTags"
          :key="tag.value"
          :class="['filter-tag', activeFilter === tag.value ? 'filter-tag-active-' + (tag.value || 'all') : '']"
          @click="onFilterTagClicked(tag.value)"
        >
          <van-icon v-if="tag.value === 'matched'" name="like" size="12" :color="activeFilter === 'matched' ? '#E8543A' : '#8A8A8A'" />
          <van-icon v-else-if="tag.value === 'interested'" name="star" size="12" :color="activeFilter === 'interested' ? '#F5C842' : '#8A8A8A'" />
          {{ tag.label }}
        </span>
      </div>
    </div>

    <!-- 一键全部已读 -->
    <div class="mark-all-read-row" v-if="matchHistoryList.some(item => !item.isRead)">
      <span class="mark-all-read-btn" @click="markAllMatchesRead">{{ $t('mark_all_read') }}</span>
    </div>

    <!-- 加载中 -->
    <div v-if="matchHistoryLoading" style="display: flex; justify-content: center; padding: 40px;">
      <van-loading size="24" />
    </div>

    <!-- 空状态 -->
    <div v-else-if="matchHistoryList.length === 0" class="matches-empty-state">
      <div class="matches-logo-wrap">
        <div class="matches-logo-aura"></div>
        <div class="matches-logo-aura matches-logo-aura-2"></div>
        <img src="/static/logo-transparent.png" class="matches-spinning-logo" />
      </div>
      <div class="matches-empty-text">{{ $t('finding_matches') }}</div>
      <div class="matches-dots"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
    </div>

    <!-- 匹配人员列表 -->
    <div v-else class="match-person-list">
      <div v-for="(item, index) in matchHistoryList" :key="index" class="match-person-item"
        @click="onMatchPersonClicked(item)">

        <!-- 上半部分：标签 + UserMsg + AI Summary + Avatar/Name/Time -->
        <div v-if="item.matchedAiSummary || item.matchedUserName || item.matchedUserMsg" class="match-person-content">
          <!-- 顶部标签行 -->
          <div class="match-person-header">
            <span :class="['match-level-badge-fancy', item.matchLevel === 'matched' ? 'badge-perfect-fancy' : 'badge-worth-fancy']">
              <van-icon :name="item.matchLevel === 'matched' ? 'like' : 'star'" />
              {{ item.matchLevel === 'matched' ? $t('label_perfect_match') : $t('label_worth_a_look') }}
            </span>
            <div v-if="!item.isRead" class="unread-dot"></div>
            <!-- Channel 标签 -->
            <template v-if="item.channel">
              <div style="flex: 1;"></div>
              <span class="channel-badge">
                <van-icon name="friends-o" size="12" color="#2A8FAD" />
                {{ item.channel }}
              </span>
            </template>
          </div>

          <!-- User Msg（优先展示，作为主内容） -->
          <div v-if="item.matchedUserMsg" class="match-person-user-msg">{{ $common.formatUserMsgPreview(item.matchedUserMsg) }}</div>

          <!-- 对方会话私有时展示提示（后端不下发内容，matchedPublic != 1） -->
          <div v-if="isPeerPrivate(item)" class="match-person-private-hint">
            <van-icon name="lock" size="12" color="#8A8A8A" />
            <span>{{ $t('match_private_hint') }}</span>
          </div>

          <!-- AI Summary（缩小 + 特殊标识） -->
          <div v-if="item.matchedAiSummary" :class="['match-person-ai-summary', item.matchedUserMsg ? 'has-user-msg' : '']">
            <span class="ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span>
            <span class="ai-summary-text">{{ item.matchedAiSummary }}</span>
          </div>

          <!-- Avatar + Name + Time -->
          <div class="match-person-meta">
            <div class="meta-avatar">
              <img v-if="item.matchedAvatar" :src="$common.getFullImageUrl(item.matchedAvatar, $config, 's')" />
              <div v-else class="avatar-placeholder-small">{{ (item.matchedUserName || $t('anonymous'))[0] }}</div>
            </div>
            <span class="meta-name">{{ item.matchedUserName }}</span>
            <div style="flex: 1;"></div>
            <span v-if="item.createdAt" class="meta-time">{{ $common.formatDateTime(item.createdAt) }}</span>
          </div>
        </div>

        <!-- Match Reason（强化展示，渐变背景占满底部） -->
        <div v-if="item.briefMatchReason" :class="['match-person-reason', item.matchLevel === 'matched' ? 'reason-matched' : 'reason-interested']">
          <span class="reason-tag">{{ $t('match_reasonTag') }}</span>
          <span class="reason-text">{{ item.briefMatchReason }}</span>
        </div>

        <!-- 空状态 -->
        <div v-if="!item.matchedAiSummary && !item.briefMatchReason && !item.matchedUserMsg && !isPeerPrivate(item)" class="match-person-empty">...</div>
      </div>

      <!-- 加载更多（已全部加载时不展示任何提示） -->
      <div v-if="matchHistoryList.length > 0 && !matchHistoryFinished" style="padding: 16px; text-align: center;">
        <van-button size="small" :loading="matchHistoryLoadingMore"
          @click="loadMoreMatchHistory" type="default">{{ $t('loading') }}</van-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Loading, Button, Toast } from 'vant'
import AdBanner from './AdBanner.vue'

Vue.use(Icon)
Vue.use(Loading)
Vue.use(Button)
Vue.use(Toast)

export default {
  name: 'Matches',
  components: {
    AdBanner
  },
  data() {
    return {
      matchHistoryList: [],
      matchHistoryLoading: false,
      matchHistoryLoadingMore: false,
      matchHistoryCurPage: 1,
      matchHistoryPageSize: 20,
      matchHistoryFinished: false,
      activeFilter: ''
    }
  },
  computed: {
    computedFilterTags() {
      return [
        { value: '', label: this.$t('filter_all') },
        { value: 'matched', label: this.$t('filter_best_connection') },
        { value: 'interested', label: this.$t('filter_worth_a_look') }
      ]
    }
  },
  mounted() {
    this.loadAllMatchHistory()
  },
  methods: {
    // 对方会话是否私有（后端对私有会话不下发 matchedUserMsg/matchedAiSummary）
    isPeerPrivate(item) {
      return item.matchedPublic !== undefined && item.matchedPublic !== null && String(item.matchedPublic) !== '1'
    },
    refresh() {
      this.matchHistoryCurPage = 1
      this.matchHistoryFinished = false
      this.loadAllMatchHistory()
    },
    onFilterTagClicked(filterValue) {
      if (this.activeFilter === filterValue) return
      this.activeFilter = filterValue
      this.matchHistoryCurPage = 1
      this.matchHistoryFinished = false
      this.matchHistoryList = []
      this.loadAllMatchHistory()
    },
    async loadAllMatchHistory(isLoadMore) {
      if (this.matchHistoryFinished && isLoadMore) return

      if (isLoadMore) {
        this.matchHistoryLoadingMore = true
      } else {
        this.matchHistoryLoading = true
      }

      try {
        const response = await axios.post('/api/chat/get_all_match_history', {
          pageSize: this.matchHistoryPageSize,
          curPage: this.matchHistoryCurPage,
          matchLevel: this.activeFilter
        })

        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          if (isLoadMore) {
            this.matchHistoryList = this.matchHistoryList.concat(details)
          } else {
            this.matchHistoryList = details
          }

          if (details.length < this.matchHistoryPageSize) {
            this.matchHistoryFinished = true
          } else {
            this.matchHistoryCurPage++
          }
        } else {
          if (!isLoadMore) {
            this.matchHistoryList = []
          }
          this.matchHistoryFinished = true
          Toast(response.data.msg || this.$t('toast_load_history_failed'))
        }
      } catch (error) {
        console.error('[Matches] Load all match history error:', error)
        if (!isLoadMore) {
          this.matchHistoryList = []
        }
        this.matchHistoryFinished = true
        Toast(this.$t('toast_load_history_failed'))
      } finally {
        this.matchHistoryLoading = false
        this.matchHistoryLoadingMore = false
        // 仅在首次加载且有数据时更新未读状态（避免空列表覆盖正确结果）
        if (!isLoadMore && this.matchHistoryList.length > 0) {
          this.updateMatchUnreadStatus()
        }
      }
    },
    loadMoreMatchHistory() {
      this.loadAllMatchHistory(true)
    },
    onMatchPersonClicked(item) {
      // 标记已读
      if (!item.isRead && item.matchDetailId) {
        axios.post('/api/chat/update_match_read', { matchDetailId: String(item.matchDetailId) }).then(() => {
          item.isRead = true
          this.updateMatchUnreadStatus()
        }).catch(err => {
          console.log('Failed to update match read:', err)
        })
      }

      const cacheKey = `match_reason_${item.sourceChatSessionId}_${item.matchedChatSessionId}`
      if (item.detailedMatchReason) {
        try {
          sessionStorage.setItem(cacheKey, item.detailedMatchReason)
        } catch (e) {
          console.warn('[Matches] Failed to cache detailedMatchReason:', e)
        }
      }

      let url = `/app/#/matched-session?currentChatSessionId=${item.sourceChatSessionId}&matchedChatSessionId=${item.matchedChatSessionId}`
      if (item.matchDetailId) {
        url += `&matchDetailId=${item.matchDetailId}`
      }
      window.open(url, '_blank')
    },
    updateMatchUnreadStatus() {
      // 列表为空时不覆盖，避免加载失败/未加载时把 checkHaveUnreadMatch 的正确结果冲掉
      if (this.matchHistoryList.length === 0) return
      const hasUnread = this.matchHistoryList.some(item => !item.isRead)
      this.$store.commit('setHasUnreadMatch', hasUnread)
      this.$store.commit('setUnreadMatchCount', this.matchHistoryList.filter(item => !item.isRead).length)
    },
    // 一键全部已读：所有未读匹配，成功后本地乐观更新
    async markAllMatchesRead() {
      try {
        await axios.post('/api/chat/mark_all_matches_read', {})
        this.matchHistoryList.forEach(item => {
          item.isRead = true
        })
        this.$store.commit('setHasUnreadMatch', false)
        this.$store.commit('setUnreadMatchCount', 0)
      } catch (error) {
        console.log('markAllMatchesRead failed:', error)
        Toast.fail(this.$t('toast_mark_all_read_failed'))
      }
    }
  }
}
</script>

<style scoped>
.matches-page {
  min-height: calc(100vh - 120px);
  background: #F7F5F2;
  padding-top: 10px;
}

.match-filter-bar {
  padding: 8px 16px;
  text-align: left;
}

/* 一键全部已读 */
.mark-all-read-row {
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 4px;
}

.mark-all-read-btn {
  font-size: 12px;
  font-weight: 600;
  color: #C93E1A;
  background: #FEF0ED;
  border-radius: 14px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.mark-all-read-btn:active {
  background: #F8A090;
}

/* tab 容器：贴合内容宽度，不再占满整行（与 client 端一致） */
.match-filter-tags {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #F7F5F2;
  border-radius: 14px;
  padding: 2px;
}

.filter-tag {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #8A8A8A;
  background: transparent;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;
  user-select: none;
}

.filter-tag:active {
  opacity: 0.8;
}

/* 激活态：白底 + 浅阴影，文字深色，图标单独着色（同 client 端） */
.filter-tag-active-all,
.filter-tag-active-matched,
.filter-tag-active-interested {
  background: #FFFFFF;
  color: #1A1A1A;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.match-person-list {
  display: flex;
  flex-direction: column;
  padding: 0 12px;
}

.match-person-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
  background: white;
  border-radius: 14px;
  border: 0.5px solid rgba(224, 221, 216, 0.6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.2s;
}

.match-person-item:active {
  background-color: #FAFAF8;
}

.match-person-content {
  padding: 10px 14px;
}

.match-person-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.match-level-badge-fancy {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.badge-perfect-fancy {
  background: linear-gradient(135deg, #E8543A, #F07058);
}

.badge-worth-fancy {
  background: linear-gradient(135deg, #F5A623, #F5C842);
}

.channel-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  background: #E8F7FB;
  font-size: 11px;
  font-weight: 700;
  color: #2A8FAD;
}

/* User Msg - 主内容展示 */
.match-person-user-msg {
  margin-top: 8px;
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  word-break: break-word;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* AI Summary - 缩小 + 特殊标识 */
.match-person-ai-summary {
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  gap: 3px;
}

.match-person-ai-summary.has-user-msg {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(224, 221, 216, 0.4);
}

.ai-summary-label {
  flex-shrink: 0;
  font-size: 10px;
  line-height: 1.3;
  color: #E8543A;
  background: #FEF0ED;
  padding: 1px 6px;
  border-radius: 4px;
  margin-right: 4px;
}

.ai-summary-text {
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.3;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.match-person-meta {
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 6px;
}

.meta-avatar {
  width: 24px;
  height: 24px;
  border-radius: 16%;
  overflow: hidden;
  flex-shrink: 0;
}

.meta-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder-small {
  width: 24px;
  height: 24px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 600;
}

.meta-name {
  font-size: 11px;
  color: #8A8A8A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-time {
  font-size: 10px;
  color: #bbb;
  white-space: nowrap;
}

/* Match Reason - 优化展示，避免审美疲劳 */
.match-person-reason {
  padding: 8px 14px 10px;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  background: #FEF0ED;
  border: 0.8px solid rgba(248, 160, 144, 0.6);
  border-radius: 0 0 14px 14px;
}

.reason-matched,
.reason-interested {
  background: #FEF0ED;
}
.reason-tag {
  flex-shrink: 0;
  font-size: 10px;
  color: #C93E1A;
  background: #fff;
  padding: 1px 6px;
  border-radius: 4px;
}

.reason-icon {
  flex-shrink: 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(232, 84, 58, 0.6);
}

.reason-text {
  font-size: 12px;
  color: #4A4A4A;
  font-weight: 400;
  line-height: 1.5;
  word-break: break-word;
}

.match-person-empty {
  padding: 8px 14px 10px;
  font-size: 11px;
  color: #8A8A8A;
}

.match-person-private-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 12px;
  color: #8A8A8A;
  line-height: 1.4;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #E05252;
}

.matches-empty-state {
  text-align: center;
  padding: 60px 40px;
}

.matches-logo-wrap {
  position: relative;
  display: inline-block;
  width: 64px;
  height: 64px;
  animation: matches-breathe 2s ease-in-out infinite;
}

.matches-logo-aura {
  position: absolute;
  top: 0;
  left: 0;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(232, 84, 58, 0.15);
  animation: matches-aura 2.5s ease-out infinite;
}

.matches-logo-aura-2 {
  animation-delay: 1.25s;
}

.matches-spinning-logo {
  position: relative;
  width: 64px;
  height: 64px;
  animation: matches-spin 4s linear infinite;
}

.matches-empty-text {
  margin-top: 20px;
  font-size: 14px;
  color: #8A8A8A;
  line-height: 1.6;
  padding: 0 32px;
}

.matches-dots {
  margin-top: 4px;
  text-align: center;
  line-height: 1;
}

.matches-dots .dot {
  display: inline-block;
  width: 5px;
  height: 5px;
  margin: 0 2px;
  border-radius: 50%;
  background: #E8543A;
  animation: matches-dot 1.4s ease-in-out infinite;
}

.matches-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.matches-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes matches-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes matches-breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

@keyframes matches-aura {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

@keyframes matches-dot {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Connections.vue